import React, {JSX, ReactNode, useEffect, useMemo} from 'react';
import {createBrowserRouter, Outlet, type RouteObject, RouterProvider, useLocation} from 'react-router-dom';
import {Box, CircularProgress} from '@mui/material';
import AppHeader from './AppHeader';
import Sidebar from './Sidebar';
import {AppPages} from './AppPages';
import CssBaseline from "@mui/material/CssBaseline";
import {useUiState} from "./uiStore";
import {createTheme, ThemeProvider} from "@mui/material/styles";
import {LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {ConfirmProvider} from "material-ui-confirm";
import {SnackbarProvider} from "notistack";
import {DialogProvider} from "./DialogProvider";
import {useAuthStore} from "./authStore";
import {NavigationError} from "../components/error/NavigationError";
import {useCurrentOfficeStore} from "../api/CurrentOfficeStore";
import ErrorPageBoundary, {toErrorPageProps} from "../components/error/ErrorPageBoundary";
import {ErrorPage} from "../components/error/ErrorPage";
import {useOfficeStore} from "../api/OfficeStore";
import {useRemoteValue} from "../util/RemoteValue";
import {translateNfcKeyDetectedEvents} from "../util/NfcReaderListener";
import {useEffectWithLifetime} from "../util/Lifetime";
import Permission from "../util/access/Permission";
import {useAccess} from "../util/access/UseAccess";
import {Access} from "../util/access/Access";

const theme = createTheme();

function AppLayout() {
    const {sidebarOpen: isSidebarOpen, sidebarWidth} = useUiState();

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', minHeight: '100vh'}}>
            <CssBaseline/>
            <AppHeader/>
            <Sidebar/>
            <Box component="main"
                 sx={{
                     flexGrow: 1,
                     py: 4,
                     padding: theme.spacing(3),
                     width: isSidebarOpen ? `calc(100% - ${sidebarWidth}px)` : '100%',
                     marginLeft: isSidebarOpen ? `${sidebarWidth}px` : 0,
                     transition: isSidebarOpen ? (
                         theme.transitions.create(['margin', 'width'], {
                             easing: theme.transitions.easing.easeOut,
                             duration: theme.transitions.duration.enteringScreen,
                         })
                     ) : (
                         theme.transitions.create(['margin', 'width'], {
                             easing: theme.transitions.easing.sharp,
                             duration: theme.transitions.duration.leavingScreen,
                         })
                     ),
                 }}
            >
                <Outlet/>
            </Box>
        </Box>
    );
}

export function PageAccess({requiredPermissions, requiredJetBrains, error, children}: {
                        requiredPermissions: readonly Permission[],
                        requiredJetBrains: boolean | undefined
                    } & ({
                        error: NavigationError,
                        children?: never,
                    } | {
                        error?: never,
                        children: (access: Access) => ReactNode,
                    })
): JSX.Element {
    const user = useAuthStore(state => state.user);
    const access = useAccess();
    const {officeId, officePathElement, onPathChange} = useCurrentOfficeStore();
    const pathname = useLocation().pathname;
    const offices = useOfficeStore(state => state.remoteValue);

    useEffect(() => {
        onPathChange(pathname);
    }, [pathname, onPathChange]);

    const officeExists = useRemoteValue(
        useMemo(() => {
            return offices.map(offices => offices.some(office => office.id === officeId))
        }, [officeId, offices]),
        null,
    );

    const navigationError = !user ? NavigationError.unauthorized()
        : !access.currentOfficeId ? (NavigationError.notFound('Office is not set'))
            : !access.accessibleOffices.canAccessOffice(access.currentOfficeId) ? NavigationError.forbiddenOffice()
                : !access.has(requiredPermissions) ? NavigationError.forbidden()
                    : !access.matchesJetBrains(requiredJetBrains) ? NavigationError.jetBrainsOnly()
                    : officeExists === false ? NavigationError.forbiddenOffice(`Cannot resolve office with ${officePathElement}`)
                        : error;

    return navigationError ? (
        <ErrorPage {...toErrorPageProps(navigationError)}/>
    ) : officeExists === null ? (
        <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh'}}>
            <CircularProgress/>
        </Box>
    ) : <>{children?.(access)}</>;
}

const router = createBrowserRouter([
    {
        path: "/offices/:officePathEl",
        element: <AppLayout/>,
        errorElement: <ErrorPageBoundary/>,
        children: [...Object.keys(AppPages)
            .map((pageKey): RouteObject => {
                const page = AppPages[pageKey];
                return {
                    path: page.path.replace(/^\//, ''),
                    element: (
                        <DialogProvider>
                            <PageAccess requiredPermissions={page.requiredPermissions} requiredJetBrains={page.requiredJetBrains}>
                                {access => typeof page.component === 'function' ? page.component(access) : page.component}
                            </PageAccess>
                        </DialogProvider>
                    ),
                };
            }),
            {
                path: '*',
                element: (
                    <PageAccess
                        requiredPermissions={[]}
                        requiredJetBrains={false}
                        error={NavigationError.notFound()}
                    />
                ),
            },
        ],
    },
    {
        path: '*',
        element: <AppLayout/>,
        errorElement: <ErrorPageBoundary/>,
        children: [{
            path: '*',
            element: (
                <PageAccess
                    requiredPermissions={[]}
                    requiredJetBrains={false}
                    error={NavigationError.notFound()}
                />
            ),
        }]
    },
]);

function App() {
    useEffectWithLifetime(lifetime => {
        translateNfcKeyDetectedEvents(lifetime);
    }, []);

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <ThemeProvider theme={theme}>
                <CssBaseline/>
                <ConfirmProvider>
                    <SnackbarProvider anchorOrigin={{vertical: 'bottom', horizontal: 'right'}} maxSnack={3}>
                        <RouterProvider router={router}/>
                    </SnackbarProvider>
                </ConfirmProvider>
            </ThemeProvider>
        </LocalizationProvider>
    );
}

export default App;
